import { Injectable } from "@angular/core";

import { Service } from "./base-service";
import { SocketService } from "./socket.service";

import { SavingsPlan } from "@models/savings_plan";
import { TradableListService } from "./currency/tradable_list.service";
import { TradableWalletListService } from "./wallet/tradable_wallet_list.service";
import { BehaviorSubject, forkJoin, Observable, of, Subject } from "rxjs";
import { InitService } from "@core-services/init.service";
import { ServiceNames } from "@models/service_enums";
import { Helper } from "@helpers/functions";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { Tradable } from "@models/interfaces/tradable";
import { UserService } from "./user.service";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { DestructorService } from "@core-services/destructor.service";
import { ModalService } from "@core-services/modal.service";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { map } from "rxjs/operators";
import { BFFAssetsService } from "@core-services/bff-assets.service";
import { BFFSecurityAsset } from "@models/asset/bff_security_asset";

const name = ServiceNames.SAVINGS;

@UntilDestroy()
@Injectable({
	providedIn: "root"
})
export class SavingsService extends Service<SavingsPlan> {
	private plansForWidget: BehaviorSubject<SavingsPlan[]> = new BehaviorSubject<SavingsPlan[]>([]);
	private plansForAccountList: BehaviorSubject<SavingsPlan[]> = new BehaviorSubject<SavingsPlan[]>([]);
	public name: string = ServiceNames.SAVINGS;

	public updateReceived$: Subject<string> = new Subject();
	public readonly hasAnyActivePendingOrPausedPlan: Observable<boolean> = this.getList().pipe(
		map((list: SavingsPlan[]) => list.some((p) => p.isActive() || p.isPending() || p.isPaused()))
	);

	public constructor(
		private apicallService: ApicallService,
		private infoService: InfoService,
		private socketService: SocketService,
		protected initService: InitService,
		protected translationService: TranslationService,
		protected destructor: DestructorService,
		protected modalService: ModalService,
		private tradableListService: TradableListService,
		private userService: UserService,
		private tradableWalletListService: TradableWalletListService,
		private bffAssetsService: BFFAssetsService
	) {
		super(initService, destructor, name);
	}

	public initSavings(): void {
		// Don't call api savings plans when the user is unverified.
		if (this.userService.user.isUnverified()) {
			this.setInitService();
		} else {
			// Fetch all active plans for SavingsWidget - will be sliced down to default item count
			this.apicallService.get("savings/plans", new ApicallOption(SavingsPlan)).subscribe(
				(plans) => {
					this.setInitService(plans);
				},
				(error) => {
					Helper.handleError(error);
					this.setInitService();
				}
			);
		}

		// Socket subscription
		this.socketService
			.joinRoom<SavingsPlan>({
				name: "savings_plan",
				isPrivate: true,
				type: SavingsPlan
			})
			.pipe(untilDestroyed(this))
			.subscribe(async (plan) => {
				switch (plan.getEvent()) {
					case "updated":
						plan = this.keepPlanExtraDetails(plan);
						this.upsert(plan);
						break;
					case "created":
						this.upsert(plan);
						// Check if the payment was fully finished - triggered only once by API => ('created' + "isActive")
						if (plan.isActive()) {
							this.getFullPlanDetails(plan);
							if (this.modalService.getCurrentModal()) {
								this.modalService.getCurrentModal().dismiss();
							}
							const { SavingsSuccessComponent } =
								await import("../components/step-summary/success-screens/savings-success/savings-success.component");
							this.modalService
								.create(SavingsSuccessComponent, { data: plan }, { backdropDismiss: true })
								.present();
						}
						break;
					case "deleted":
						plan = this.keepPlanExtraDetails(plan);
						this.upsert(plan); // We currently don't delete it from the list. We show it with red deleted state
						break;
					default:
						break;
				}
				this.updateReceived$.next(plan.getEvent());
			});

		this.socketService
			.joinCollectionRoom<SavingsPlan>(
				{
					name: "savings_plans",
					type: SavingsPlan,
					isPrivate: true
				},
				"recurring_payment_plans"
			)
			.pipe(untilDestroyed(this))
			.subscribe((collection) => {
				collection.forEach((plan: SavingsPlan) => {
					plan = this.keepPlanExtraDetails(plan);
					this.upsert(plan);
					this.updateReceived$.next("");
				});
			});
	}

	private setInitService(list: SavingsPlan[] = []): void {
		this.list = list;
		this.initService.init(this.name);
	}

	// Delete
	public cancelPlan(plan: SavingsPlan): void {
		this.apicallService.deleteRequest("savings/plans/" + plan.getId(), new ApicallOption(SavingsPlan)).subscribe(
			(p) => {
				p[0] = this.keepPlanExtraDetails(p[0]);
				this.upsert(p[0]);
				const asset = this.getAsset(this.getAssetId(plan));
				this.infoService.setSuccess(
					this.translationService.translate("savings_plan_with_plans_details_canceled_successfully", [
						asset.isToken() ? asset.getName() : asset.getSymbol(false, true)
					])
				);
			},
			(error) => Helper.handleError(error)
		);
	}

	// Pause / Continue
	public updatePauseContinueAPI(plan: SavingsPlan, newState: string): void {
		this.apicallService
			.put("savings/plans/" + plan.getId(), new ApicallOption(SavingsPlan, { state: newState }))
			.subscribe(
				(p) => {
					p[0] = this.keepPlanExtraDetails(p[0]);
					this.upsert(p[0]);
				},
				(error) => Helper.handleError(error)
			);
	}

	// Full plan details
	public getFullPlanDetails(plan: SavingsPlan): void {
		this.apicallService.get("savings/plans/" + plan.getId(), new ApicallOption(SavingsPlan)).subscribe(
			(p) => {
				this.upsert(p[0]);
			},
			(error) => Helper.handleError(error)
		);
	}

	// Get payment details
	public getPaymentDetails(plan: SavingsPlan): void {
		this.apicallService.post("savings/plans/" + plan.getId() + "/pay", new ApicallOption(SavingsPlan)).subscribe(
			async (p) => {
				this.upsert(p[0]);
				const { DepositPendingComponent } =
					await import("@modules/main/components/step-summary/success-screens/deposit-pending/deposit-pending.component");
				this.modalService
					.create(
						DepositPendingComponent,
						{
							transaction: p[0].getSavingsPayments()[0].getFiatTransaction(),
							isSavings: true
						},
						{ backdropDismiss: false }
					)
					.present();
			},
			(error) => Helper.handleError(error)
		);
	}

	public hasActiveWidgetPlans(): boolean {
		return this.plansForWidget.getValue().length > 0;
	}

	public getPlansForWidget(): Observable<SavingsPlan[]> {
		return this.plansForWidget.asObservable();
	}

	public setPlansForWidget(plans: SavingsPlan[]): void {
		this.plansForWidget.next(plans);
	}

	public getPlansForAccount(): Observable<SavingsPlan[]> {
		return this.plansForAccountList.asObservable();
	}

	public setPlansForAccount(plans: SavingsPlan[]): void {
		this.plansForAccountList.next(plans);
	}

	public get activePendingPlans(): SavingsPlan[] {
		return this.list.filter((p) => p.isActive());
	}

	public getUserHasSavingsPlan(isWidget: boolean = false): boolean {
		if (isWidget) {
			return this.list && !!this.plansForWidget && this.plansForWidget.getValue().length > 0;
		}
		return this.list && this.list.length > 0;
	}

	private keepPlanExtraDetails(p: SavingsPlan): SavingsPlan {
		if (this.hasItem(p)) {
			const existingPlan = this.getItemById(p.getId());
			if (existingPlan.getFinishedBuys()) {
				p.setFinishedBuys(existingPlan.getFinishedBuys());
			}
			if (existingPlan.getTotalSpent()) {
				p.setTotalSpent(existingPlan.getTotalSpent());
			}
		}
		return p;
	}

	public getAsset(tradableId: string): Tradable {
		return this.tradableListService.getItemById(tradableId);
	}

	public getAssetId(plan: SavingsPlan): string {
		return this.getAssetWallet(plan.getAssetWalletId()).getCurrencyId();
	}

	public getAssetWallet(walletId: string): TradableWallet {
		return this.tradableWalletListService.getItemById(walletId);
	}

	public upsertBffAssetsReturnPlansHelper$(
		missingPlans: SavingsPlan[],
		allPlans: SavingsPlan[]
	): Observable<SavingsPlan[]> {
		if (missingPlans.length > 0) {
			return this.getBffAssets(missingPlans).pipe(
				map((assets) => {
					for (const asset of assets) {
						this.tradableListService.upsert(asset);
					}
					return allPlans;
				})
			);
		}
		return of(allPlans);
	}

	public checkAssetsInPlan(plans: SavingsPlan[]): SavingsPlan[] {
		return plans.filter((plan) => !this.tradableListService.getItemById(plan.getCurrencyId()));
	}

	public getBffAssets(plans: SavingsPlan[]): Observable<BFFSecurityAsset[]> {
		const assetIds = plans.map((plan) => plan.getCurrencyId());
		const obsList$ = assetIds.map((pid) => this.getBffAsset(pid));
		return forkJoin(obsList$);
	}

	private getBffAsset(id: string): Observable<BFFSecurityAsset> {
		return this.bffAssetsService
			.getAssetFetch({ input: { idInternal: Number(id) } })
			.pipe(map((asset) => new BFFSecurityAsset(asset)));
	}
}
